import type { AnalyticsTracker } from '@/tracking/analytics/analyticsTracker'
import { ANCHOR_PAGE_EVENT_NAMES } from './anchor_page_event_names'
import { useAnchorPageStore } from './anchor_page.store'
import type { EditedObjective } from './anchor_page_types'

const ANNUAL_LEVEL = 'Annual'
const ALL_OBJECTIVES_LEVEL = 'all'

/**
 * Records the teacher's edits into the save payload and sends the events the anchor page sends.
 * Takes the page's tracker rather than injecting one, so events carry the context the page
 * itself provides with getChildContext.
 */
export function useAnchorPageTracking(analytics: AnalyticsTracker) {
  const anchorPageStore = useAnchorPageStore()

  function editAnnualText(text: string) {
    anchorPageStore.setPrimaryGoalText(text)
    analytics.sendEvent(ANCHOR_PAGE_EVENT_NAMES.EDITED_GOAL_TEXT, { obj_level: ANNUAL_LEVEL, text })
  }

  function editObjectiveText(edit: EditedObjective) {
    anchorPageStore.setObjective(edit)
    const objLevel = edit.isAllObjectives ? ALL_OBJECTIVES_LEVEL : edit.objective.level
    analytics.sendEvent(ANCHOR_PAGE_EVENT_NAMES.EDITED_GOAL_TEXT, { obj_level: objLevel, text: edit.objective.text })
  }

  function trackPageViewed(standardCodes: string[] | undefined) {
    analytics.sendEvent(ANCHOR_PAGE_EVENT_NAMES.VIEWED_PAGE, { standard_codes: standardCodes })
  }

  function trackAnnualResourcesToggled(isShown: boolean) {
    analytics.sendEvent(ANCHOR_PAGE_EVENT_NAMES.TOGGLED_ANNUAL_RESOURCES, { annual_resources_shown: isShown })
  }

  function trackQuarterlyObjectivesToggled(isShown: boolean) {
    analytics.sendEvent(ANCHOR_PAGE_EVENT_NAMES.TOGGLED_QUARTERLY_OBJECTIVES, { quarterly_objectives_shown: isShown })
  }

  function trackSdiStrategiesToggled(isShown: boolean) {
    analytics.sendEvent(ANCHOR_PAGE_EVENT_NAMES.TOGGLED_SDI_STRATEGIES, { sdi_strategies_shown: isShown })
  }

  function trackExampleContentToggled(isShown: boolean) {
    analytics.sendEvent(ANCHOR_PAGE_EVENT_NAMES.CLICKED_EXAMPLE_CONTENT, { goal_example_shown: isShown })
  }

  return {
    editAnnualText,
    editObjectiveText,
    trackPageViewed,
    trackAnnualResourcesToggled,
    trackQuarterlyObjectivesToggled,
    trackSdiStrategiesToggled,
    trackExampleContentToggled
  }
}
